import { useSyncExternalStore } from 'react';
import { Clock, User, Trash2 } from '../../icons';
import { TASK_PRIORITY_META, type TaskDto, type TaskPriority } from '../../api/tasks';
import { tTask, getTaskLang, subscribeTaskLang } from '../../lib/task-i18n';
// 2026-08-26 — 카드가 스스로 상태를 말하게: 진행률 막대 + 기한 색. 신규 파일(스타일만 추가).
import { dueState } from '../ProjectPage/my-task-due';
import './task-viz.css';
// 2026-09-02 — 카드 제목 옆 [🗑]. 신규 파일이라 기존 카드 규칙은 건드리지 않는다.
import './TaskCardActions.css';

interface TaskCardProps {
  task: TaskDto;
  dragging: boolean;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  /** v2.7.2 — 카드 클릭 → 상세 레이어팝업 (미전달 시 기존 동작 무변경). */
  onOpen?: () => void;
  /**
   * 담당자 이름을 찾기 위한 팀원 목록 (2026-08-31). 미전달이면 인원수로만 표기한다 —
   * 목록을 못 받은 화면에서도 카드가 깨지지 않게 optional 로 둔다.
   */
  members?: { userId: string; name: string }[];
  /**
   * 카드에서 바로 삭제 (2026-09-02). **주지 않으면 버튼도 뜨지 않는다** —
   * 목록보기 등 이 prop 을 안 넘기는 기존 호출부는 종전과 완전히 같은 카드를 그린다.
   * 지울 수 있는지(작성자 또는 MANAGER+)의 판정은 호출부가 하고, 여기서는 그리기만 한다.
   */
  onDelete?: () => void;
}

/**
 * TaskCard (P3-TASK-FE-3) — kanban / list 공용.
 * 시안의 task chip + assignee + due + tags + priority.
 */
export function TaskCard({ task, dragging, onDragStart, onDragEnd, onOpen, members, onDelete }: TaskCardProps) {
  useSyncExternalStore(subscribeTaskLang, getTaskLang, getTaskLang);
  const priorityMeta = TASK_PRIORITY_META[task.priority as TaskPriority] ?? null;
  /*
   * 기한 색 (2026-08-26) — 종전에는 지난 날짜도 앞으로의 날짜도 같은 회색이라,
   * 카드를 열어 봐야 늦은 건지 알았다. 판정은 내 업무 화면과 **같은 함수**를 쓴다
   * (화면마다 '지연' 의 뜻이 다르면 숫자가 어긋난다).
   */
  const ds = dueState(task.dueDate, task.status);
  const dueTone = ds === 'overdue' || ds === 'today' || ds === 'tomorrow' ? ` tvz-due--${ds}` : '';
  /* 진행률 — 값이 있고 미완료일 때만. 0%(기본값)까지 그리면 모든 카드에 빈 막대가 깔린다. */
  const progress = task.status !== 'DONE' ? (task.progress ?? 0) : 0;
  /*
   * 담당자 표기 (2026-08-31) — 종전에는 1명일 때만 `#<userId>` 를 그려 **내부 사용자 ID 가
   * 화면에 노출**됐다(단위시험 AXI-TSK-01-TC002). 목록보기·상세 팝업과 같은 기준으로 맞춘다:
   * 1명이면 이름, 여럿이면 '{n}명'. 팀원 목록에서 못 찾은 사람은 ID 를 드러내는 대신
   * 인원수로 물러선다(TaskDetailModal 은 상세라 `#id` 폴백을 쓰지만, 카드는 좁아 의미가 없다).
   */
  const assigneeLabel = (() => {
    if (task.assignees.length === 1) {
      const found = members?.find((m) => m.userId === task.assignees[0]);
      if (found) return found.name;
    }
    return tTask('task.card.assignee_count_short', { n: task.assignees.length });
  })();
  return (
    <div
      className={`task-card ${dragging ? 'task-card--dragging' : ''}${onOpen ? ' task-card--clickable' : ''}`}
      data-priority={task.priority}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      role="article"
      aria-label={onOpen ? tTask('task.card.open_aria', { title: task.title }) : task.title}
      onClick={onOpen}
      onKeyDown={onOpen ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } } : undefined}
      tabIndex={onOpen ? 0 : undefined}
    >
      <div className="task-card-header">
        {priorityMeta && (
          <span
            className="task-card-priority"
            style={{ background: priorityMeta.color }}
            title={tTask('task.card.priority_tooltip', { label: priorityMeta.label })}
          >
            {priorityMeta.label}
          </span>
        )}
        <span className="task-card-title">{task.title}</span>
        {/*
          제목 옆 [🗑] (2026-09-02) — 카드를 열지 않고 그 자리에서 지운다.
          카드 전체가 클릭(상세 열기)·드래그(상태 변경) 대상이라 셋 다 떼어 놓는다:
          click 은 stopPropagation, Enter/Space 는 keydown 이 부모로 올라가면 상세까지 열리므로
          역시 멈추고, draggable=false 로 이 버튼에서 드래그가 시작되지 않게 한다.
        */}
        {onDelete && (
          <span className="task-card-actions">
            <button
              type="button"
              className="task-card-actbtn task-card-actbtn--danger"
              draggable={false}
              aria-label={tTask('task.card.delete_aria', { title: task.title })}
              title={tTask('task.card.delete')}
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation(); }}
            >
              <Trash2 size={13} aria-hidden />
            </button>
          </span>
        )}
      </div>

      {task.description && (
        <p className="task-card-description">{task.description}</p>
      )}

      {task.tags.length > 0 && (
        <div className="task-card-tags">
          {task.tags.map((tag) => (
            <span key={tag} className="task-card-tag">#{tag}</span>
          ))}
        </div>
      )}

      <div className="task-card-meta">
        {task.assignees.length > 0 && (
          <span className="task-card-meta-item" title={tTask('task.card.assignee_count_tooltip', { n: task.assignees.length })}>
            <User size={12} />
            {assigneeLabel}
          </span>
        )}
        {task.dueDate && (
          <span className={`task-card-meta-item${dueTone}`} title={tTask('task.card.due_tooltip', { date: task.dueDate })}>
            <Clock size={12} />
            {task.dueDate}
          </span>
        )}
      </div>

      {progress > 0 && (
        <span
          className="tvz-cardprogress"
          role="img"
          aria-label={tTask('task.progress.aria', { percent: String(progress) })}
        >
          <span className="tvz-cardprogress-fill" style={{ width: `${Math.min(progress, 100)}%` }} />
        </span>
      )}
    </div>
  );
}
